import React, { useState, useEffect, useRef } from 'react';
import './Navbar.css';
import logo from '../../assets/images/logoname.svg';

const Navbar = ({ handleLogin }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const isNavigating = useRef(false);

  // Scroll spy functionality
  useEffect(() => {
    const sections = ['home', 'how-it-works', 'core-capabilities', 'ai-intelligence', 'help-faqs'];
    let scrollTimeout;
    
    const handleScroll = () => {
      // Skip if we're currently navigating programmatically
      if (isNavigating.current) return;
      
      // Clear any existing timeout
      clearTimeout(scrollTimeout);
      
      // Set a small delay to avoid rapid changes during smooth scrolling
      scrollTimeout = setTimeout(() => {
        const scrollPosition = window.scrollY + window.innerHeight * 0.25; // 25% from top
        let currentSection = 'home';
        
        // Find which section we're currently in
        for (const sectionId of sections) {
          const element = document.getElementById(sectionId);
          if (element) {
            const rect = element.getBoundingClientRect();
            const elementTop = rect.top + window.scrollY;
            
            if (scrollPosition >= elementTop) {
              currentSection = sectionId;
            }
          }
        }
        
        setActiveSection(currentSection);
      }, 150); // 150ms delay to smooth out rapid changes
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const scrollToSection = (sectionId) => {
    // Immediately set the active section and flag that we're navigating
    setActiveSection(sectionId);
    setIsMobileMenuOpen(false);
    isNavigating.current = true;
    
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      
      // Reset navigation flag after scroll completes
      setTimeout(() => {
        isNavigating.current = false;
      }, 1500); // Allow extra time for smooth scroll to complete
    } else {
      isNavigating.current = false;
    }
  };

  const handleLogoClick = () => {
    setActiveSection('home');
    isNavigating.current = true;
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    setTimeout(() => {
      isNavigating.current = false;
    }, 1500);
  };

  return (
    <header className="header">
      <div className="header-container">
        {/* Logo - Positioned absolutely on the left */}
        <div className="logo" onClick={handleLogoClick}>
          <img src={logo} alt="Logo" className="logo-img" />
        </div>

        {/* Centered Navigation */}
        <nav className={`nav-menu ${isMobileMenuOpen ? 'nav-menu-open' : ''}`}>
        
          <button 
            className={`nav-item ${activeSection === 'how-it-works' ? 'active' : ''}`}
            onClick={() => scrollToSection('how-it-works')}
          >
            How it Works
          </button>
          <button 
            className={`nav-item ${activeSection === 'core-capabilities' ? 'active' : ''}`}
            onClick={() => scrollToSection('core-capabilities')}
          >
            Core Capabilities
          </button>
          <button 
            className={`nav-item ${activeSection === 'ai-intelligence' ? 'active' : ''}`}
            onClick={() => scrollToSection('ai-intelligence')}
          >
            AI Intelligence
          </button>
          <button 
            className={`nav-item ${activeSection === 'help-faqs' ? 'active' : ''}`}
            onClick={() => scrollToSection('help-faqs')}
          >
            Help & FAQ's
          </button>
        </nav>

        {/* Login - Positioned absolutely on the right */}
        <div className="login-section">
          <button className="login-btn" onClick={handleLogin}>
            Login
          </button>
        </div>

        <button 
          className="mobile-menu-btn"
          onClick={toggleMobileMenu}
          aria-label="Toggle mobile menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </header>
  );
};

export default Navbar; 